/**
 * Carousel slide captions are stored as HTML **inner** of the caption <p>
 * (no wrapping <p> in JSON) so renderHTML can inject marks consistently.
 */

export function captionToTipTapHtml(caption) {
  if (caption == null || caption === '') return '<p></p>';
  const raw = String(caption);
  const trimmed = raw.trim();
  if (!trimmed) return '<p></p>';

  // Legacy storage path: the entire value is already a single <p>...</p>.
  // Keep it as-is so paragraph attributes survive the round trip.
  if (trimmed.startsWith('<')) {
    const tpl = document.createElement('template');
    tpl.innerHTML = trimmed;
    const root = tpl.content;
    if (root.childElementCount === 1 && root.firstElementChild?.tagName === 'P') {
      return trimmed;
    }
  }

  // Everything else is treated as the *inner* HTML of the caption paragraph
  // (text and/or inline marks like <a class="tiptap-link" href="...">).
  // Wrap it in a single <p> so TipTap parses inline marks instead of
  // textContent-escaping the markup (which would render as literal "<a ...>").
  return `<p>${raw}</p>`;
}

export function serializeCaptionEditorHtml(editor) {
  if (!editor || editor.isDestroyed) return '';
  const html = editor.getHTML().trim();
  const m = html.match(/^<p([^>]*)>([\s\S]*)<\/p>$/i);
  return m ? m[2] : html;
}

const CAPTION_RENDER_TAGS = new Set([
  'a',
  'span',
  'strong',
  'b',
  'em',
  'i',
  'u',
  's',
  'strike',
  'del',
  'code',
  'br',
  'sub',
  'sup',
  'mark',
  'small',
]);

function allowAttr(name) {
  const n = name.toLowerCase();
  if (n === 'class' || n === 'href' || n === 'target' || n === 'rel' || n === 'title') return true;
  if (n.startsWith('data-ps-')) return true;
  return false;
}

function flattenChildSpecs(node) {
  return Array.from(node.childNodes)
    .flatMap((ch) => {
      const r = domNodeToOutputSpec(ch);
      if (r === '' || r == null) return [];
      return [r];
    });
}

/**
 * Converts caption inner HTML to ProseMirror DOMOutputSpec fragments for renderHTML.
 */
export function domNodeToOutputSpec(node) {
  if (node.nodeType === Node.TEXT_NODE) {
    const t = node.textContent ?? '';
    return t;
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return '';

  const tag = node.tagName.toLowerCase();

  if (tag === 'br') {
    return ['br'];
  }

  if (tag === 'script' || tag === 'style' || tag === 'iframe') {
    return '';
  }

  if (!CAPTION_RENDER_TAGS.has(tag)) {
    const inner = flattenChildSpecs(node);
    if (inner.length === 0) return '';
    if (inner.length === 1) return inner[0];
    return inner;
  }

  const attrs = {};
  if (node.hasAttributes()) {
    for (const a of node.attributes) {
      if (allowAttr(a.name)) attrs[a.name] = a.value;
    }
  }

  const childSpecs = flattenChildSpecs(node);
  if (childSpecs.length === 0) return [tag, attrs];
  if (childSpecs.length === 1) return [tag, attrs, childSpecs[0]];
  return [tag, attrs, ...childSpecs];
}

export function captionInnerHtmlToRenderChildren(inner) {
  if (inner == null || String(inner).trim() === '') return '';
  const tpl = document.createElement('template');
  tpl.innerHTML = `<span>${String(inner)}</span>`;
  const span = tpl.content.firstChild;
  if (!span) return '';
  return domNodeToOutputSpec(span);
}
